/**
 * Invite Member Dialog
 * Multi-step dialog for inviting collaborators to a lead collaboration
 * Supports all commission models: percentage_split, fixed_amount, milestone_based
 */

import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { AutoComplete } from 'primereact/autocomplete';
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { Divider } from 'primereact/divider';
import { Steps } from 'primereact/steps';
import { Rating } from 'primereact/rating';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';

import {
    collaborationService,
    getRoleConfig,
    getContributionLevelConfig,
    formatCurrency,
    MEMBER_ROLES,
    CONTRIBUTION_LEVELS
} from '../../services/collaborationService';

// Role options for invitation
const ROLE_OPTIONS = [
    { value: 'collaborator', label: 'Collaborator', description: 'Active team member working on the lead' },
    { value: 'consultant', label: 'Consultant', description: 'Advisory role with specific expertise' }
];

// Contribution level options
const CONTRIBUTION_OPTIONS = Object.values(CONTRIBUTION_LEVELS).map(c => ({
    value: c.value,
    label: c.label,
    description: c.description
}));

// Suggested amounts for fixed_amount model
const SUGGESTED_AMOUNTS = {
    collaborator: [5000, 10000, 15000, 20000],
    consultant: [2000, 5000, 7500, 10000]
};

const InviteMemberDialog = ({
    visible,
    onHide,
    collaborationId,
    commissionModel = 'percentage_split',
    existingMembers = [],
    currentAllocation = 0, // For percentage_split - already allocated %
    availableMilestones = [], // For milestone_based
    onSuccess
}) => {
    const toast = useRef(null);

    // Step state
    const [activeStep, setActiveStep] = useState(0);

    // Agent search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);

    // Commission terms state
    const [role, setRole] = useState('collaborator');
    // Note: commissionPercentage stores OWNER's percentage (member gets 100 - this)
    const [commissionPercentage, setCommissionPercentage] = useState(90);
    const [commissionFixedAmount, setCommissionFixedAmount] = useState(0);
    const [selectedMilestones, setSelectedMilestones] = useState([]);
    const [milestoneAmounts, setMilestoneAmounts] = useState({});

    // Additional settings state
    const [contributionLevel, setContributionLevel] = useState('medium');
    const [notes, setNotes] = useState('');

    // Submission state
    const [submitting, setSubmitting] = useState(false);

    // Steps configuration
    const steps = [
        { label: 'Select Agent' },
        { label: 'Define Terms' },
        { label: 'Send Invite' }
    ];

    // Reset form when dialog opens
    useEffect(() => {
        if (visible) {
            setActiveStep(0);
            setSearchQuery('');
            setSearchResults([]);
            setSelectedAgent(null);
            setRole('collaborator');
            setCommissionPercentage(90); // Owner's percentage
            setCommissionFixedAmount(0);
            setSelectedMilestones([]);
            setMilestoneAmounts({});
            setContributionLevel('medium');
            setNotes('');
        }
    }, [visible]);

    // Search agents
    const searchAgents = async (query) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        try {
            const excludeIds = existingMembers.map(m => m.profile_id);
            const response = await collaborationService.searchUsersToInvite(query, {
                limit: 10,
                exclude_ids: excludeIds
            });

            if (response.success) {
                const results = response.data?.results || response.data || [];
                setSearchResults(results);
            }
        } catch (error) {
            console.error('Failed to search agents:', error);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle agent selection
    const handleAgentSelect = (e) => {
        setSelectedAgent(e.value);
        setSearchQuery('');
    };

    // Calculate remaining percentage for owner after this allocation
    // commissionPercentage = owner's intended share, member gets (100 - commissionPercentage)
    // remaining = owner's share - already allocated to other members
    const memberShare = 100 - commissionPercentage;
    const remainingPercentage = 100 - currentAllocation - memberShare;

    // Calculate total milestone amount for this member
    const totalMilestoneAmount = selectedMilestones.reduce((sum, id) => {
        return sum + (milestoneAmounts[id] || 0);
    }, 0);

    // Validation
    const canProceedStep1 = selectedAgent !== null;
    const canProceedStep2 = () => {
        switch (commissionModel) {
            case 'percentage_split':
                // commissionPercentage is owner's %, memberShare is member's %
                return memberShare >= 0 && memberShare <= 100 && remainingPercentage >= 0;
            case 'fixed_amount':
                return commissionFixedAmount > 0;
            case 'milestone_based':
                return selectedMilestones.length > 0 && totalMilestoneAmount > 0;
            default:
                return false;
        }
    };

    // Handle milestone toggle
    const toggleMilestone = (milestoneId) => {
        if (selectedMilestones.includes(milestoneId)) {
            setSelectedMilestones(prev => prev.filter(id => id !== milestoneId));
            setMilestoneAmounts(prev => {
                const updated = { ...prev };
                delete updated[milestoneId];
                return updated;
            });
        } else {
            setSelectedMilestones(prev => [...prev, milestoneId]);
            const milestone = availableMilestones.find(m => m.id === milestoneId);
            if (milestone?.amount) {
                setMilestoneAmounts(prev => ({ ...prev, [milestoneId]: milestone.amount }));
            }
        }
    };

    // Update milestone amount
    const updateMilestoneAmount = (milestoneId, amount) => {
        setMilestoneAmounts(prev => ({ ...prev, [milestoneId]: amount }));
    };

    // Submit invitation
    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const inviteData = {
                profile_id: selectedAgent.id || selectedAgent.profile_id,
                role,
                contribution_level: contributionLevel,
                notes
            };

            // Add commission data based on model
            switch (commissionModel) {
                case 'percentage_split':
                    // Convert owner's % to member's % for backend
                    inviteData.commission_percentage = 100 - commissionPercentage;
                    break;
                case 'fixed_amount':
                    inviteData.commission_fixed_amount = commissionFixedAmount;
                    break;
                case 'milestone_based':
                    inviteData.milestones = selectedMilestones.map(id => ({
                        milestone_id: id,
                        amount: milestoneAmounts[id] || 0
                    }));
                    break;
            }

            const response = await collaborationService.inviteMember(collaborationId, inviteData);

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Invitation Sent!',
                    detail: `${selectedAgent.name} has been invited to collaborate`
                });
                onSuccess?.(response.data);
                onHide();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to send invitation'
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Agent item template for autocomplete
    const agentItemTemplate = (agent) => (
        <div className="flex align-items-center gap-2 p-1">
            <Avatar
                label={agent.name?.charAt(0) || 'U'}
                shape="circle"
                className="bg-primary text-white"
                style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.65rem' }}
            />
            <div className="flex-1">
                <div className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>{agent.name}</div>
                {agent.company && (
                    <div className="text-500" style={{ fontSize: '0.65rem' }}>{agent.company}</div>
                )}
                {agent.specializations && agent.specializations.length > 0 && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                        {agent.specializations.slice(0, 3).map((spec, idx) => (
                            <Chip key={idx} label={spec} style={{ fontSize: '0.55rem' }} />
                        ))}
                    </div>
                )}
            </div>
            <div className="text-right">
                {agent.rating > 0 && (
                    <div className="flex align-items-center gap-1 mb-1">
                        <Rating value={agent.rating} readOnly cancel={false} stars={5} className="text-xs" />
                    </div>
                )}
                {agent.success_rate > 0 && (
                    <div className="text-green-600 font-medium" style={{ fontSize: '0.65rem' }}>
                        {agent.success_rate}% success
                    </div>
                )}
            </div>
        </div>
    );

    // Render Step 1: Select Agent
    const renderStep1 = () => (
        <div className="step-content">
            <div className="mb-3">
                <label className="block text-900 font-medium mb-2" style={{ fontSize: '0.75rem' }}>
                    <i className="pi pi-search mr-2" style={{ fontSize: '0.7rem' }} />
                    Search for Agent
                </label>
                <AutoComplete
                    value={searchQuery}
                    suggestions={searchResults}
                    completeMethod={(e) => searchAgents(e.query)}
                    field="name"
                    onChange={(e) => setSearchQuery(e.value)}
                    onSelect={handleAgentSelect}
                    itemTemplate={agentItemTemplate}
                    placeholder="Search by name, email, or phone..."
                    className="w-full"
                    inputClassName="w-full"
                    inputStyle={{ fontSize: '0.7rem' }}
                    delay={300}
                    minLength={2}
                />
                <small className="text-500 mt-1 block" style={{ fontSize: '0.6rem' }}>
                    Type at least 2 characters to search
                </small>
            </div>

            {searchLoading && (
                <div className="flex justify-content-center py-3">
                    <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                </div>
            )}

            {/* Selected Agent Preview */}
            {selectedAgent && (
                <div className="surface-100 border-round p-3 mt-3">
                    <div className="flex align-items-center justify-content-between mb-2">
                        <span className="text-500 font-medium" style={{ fontSize: '0.65rem' }}>Selected Agent</span>
                        <Button
                            icon="pi pi-times"
                            aria-label="Remove selected agent"
                            className="p-button-rounded p-button-text p-button-sm"
                            style={{ fontSize: '0.65rem', width: '1.5rem', height: '1.5rem' }}
                            onClick={() => setSelectedAgent(null)}
                        />
                    </div>
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            label={selectedAgent.name?.charAt(0) || 'U'}
                            shape="circle"
                            className="bg-primary text-white"
                            style={{ width: '2.25rem', height: '2.25rem', fontSize: '0.8rem' }}
                        />
                        <div className="flex-1">
                            <div className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>{selectedAgent.name}</div>
                            {selectedAgent.email && (
                                <div className="text-500" style={{ fontSize: '0.65rem' }}>
                                    <i className="pi pi-envelope mr-1" style={{ fontSize: '0.6rem' }} />
                                    {selectedAgent.email}
                                </div>
                            )}
                            {selectedAgent.phone && (
                                <div className="text-500" style={{ fontSize: '0.65rem' }}>
                                    <i className="pi pi-phone mr-1" style={{ fontSize: '0.6rem' }} />
                                    {selectedAgent.phone}
                                </div>
                            )}
                            {selectedAgent.company && (
                                <div className="text-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                    <i className="pi pi-building mr-1" style={{ fontSize: '0.6rem' }} />
                                    {selectedAgent.company}
                                </div>
                            )}
                        </div>
                    </div>
                    {selectedAgent.specializations && selectedAgent.specializations.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                            {selectedAgent.specializations.map((spec, idx) => (
                                <Chip key={idx} label={spec} style={{ fontSize: '0.6rem' }} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // Render Step 2: Define Terms
    const renderStep2 = () => (
        <div className="step-content">
            {/* Role Selection */}
            <div className="field mb-3">
                <label className="block text-900 font-medium mb-2" style={{ fontSize: '0.75rem' }}>
                    <i className="pi pi-user-edit mr-2" style={{ fontSize: '0.7rem' }} />
                    Role
                </label>
                <Dropdown
                    value={role}
                    options={ROLE_OPTIONS}
                    onChange={(e) => setRole(e.value)}
                    optionLabel="label"
                    optionValue="value"
                    className="w-full"
                    style={{ fontSize: '0.7rem' }}
                    itemTemplate={(option) => (
                        <div className="py-1">
                            <div className="font-medium" style={{ fontSize: '0.7rem' }}>{option.label}</div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>{option.description}</div>
                        </div>
                    )}
                />
            </div>

            <Divider className="my-2" />

            {/* Commission Terms - Based on Model */}
            <div className="field mb-3">
                <label className="block text-900 font-medium mb-2" style={{ fontSize: '0.75rem' }}>
                    <i className="pi pi-chart-pie mr-2" style={{ fontSize: '0.7rem' }} />
                    Commission
                </label>

                {/* Percentage Split Model */}
                {commissionModel === 'percentage_split' && (
                    <div>
                        <div className="flex justify-content-between mb-2 font-semibold" style={{ fontSize: '0.7rem' }}>
                            <span className="text-green-600">You (Owner): {commissionPercentage}%</span>
                            <span className="text-blue-500">Member: {100 - commissionPercentage}%</span>
                        </div>
                        <Slider
                            value={commissionPercentage}
                            onChange={(e) => setCommissionPercentage(e.value)}
                            min={0}
                            max={100}
                            step={1}
                            className="w-full split-slider-invite mb-2"
                        />
                        <div className="flex justify-content-between text-500 mb-2" style={{ fontSize: '0.6rem' }}>
                            <span>0%</span>
                            <span>100%</span>
                        </div>
                        <div className="flex justify-content-between" style={{ fontSize: '0.65rem' }}>
                            <span className="text-500">
                                Currently allocated: {currentAllocation}%
                            </span>
                            <span className={remainingPercentage < 0 ? 'text-red-500 font-bold' : 'text-500'}>
                                Remaining after: {remainingPercentage}%
                            </span>
                        </div>
                        {remainingPercentage < 0 && (
                            <Message
                                severity="error"
                                text="Total allocation exceeds 100%. Please reduce the percentage."
                                className="mt-2 w-full"
                            />
                        )}
                        <style>{`
                            .split-slider-invite.p-slider {
                                background: #3B82F6 !important;
                                height: 8px !important;
                                border-radius: 4px !important;
                            }
                            .split-slider-invite .p-slider-range {
                                background: #22C55E !important;
                                border-radius: 4px 0 0 4px !important;
                            }
                            .split-slider-invite .p-slider-handle {
                                width: 18px !important;
                                height: 18px !important;
                                margin-top: -5px !important;
                                background: white !important;
                                border: 2px solid #64748b !important;
                                box-shadow: 0 1px 4px rgba(0,0,0,0.2) !important;
                            }
                            .split-slider-invite .p-slider-handle:hover {
                                border-color: #22C55E !important;
                            }
                            .compact-invite-dialog .p-dialog-header {
                                padding: 0.75rem 1rem !important;
                            }
                            .compact-invite-dialog .p-dialog-content {
                                padding: 0.5rem 1rem !important;
                            }
                            .compact-invite-dialog .p-dialog-footer {
                                padding: 0.5rem 1rem !important;
                            }
                            .compact-steps .p-steps .p-steps-item .p-menuitem-link .p-steps-title {
                                font-size: 0.65rem !important;
                            }
                            .compact-steps .p-steps .p-steps-item .p-menuitem-link .p-steps-number {
                                font-size: 0.65rem !important;
                                width: 1.5rem !important;
                                height: 1.5rem !important;
                                min-width: 1.5rem !important;
                            }
                        `}</style>
                    </div>
                )}

                {/* Fixed Amount Model */}
                {commissionModel === 'fixed_amount' && (
                    <div>
                        <InputNumber
                            value={commissionFixedAmount}
                            onValueChange={(e) => setCommissionFixedAmount(e.value)}
                            mode="currency"
                            currency="INR"
                            locale="en-IN"
                            className="w-full mb-2"
                            placeholder="Enter commission amount"
                            inputStyle={{ fontSize: '0.7rem' }}
                        />
                        <div className="text-500 mb-2" style={{ fontSize: '0.65rem' }}>Suggested amounts:</div>
                        <div className="flex gap-2 flex-wrap">
                            {SUGGESTED_AMOUNTS[role]?.map((amount) => (
                                <Button
                                    key={amount}
                                    label={formatCurrency(amount)}
                                    className={`p-button-sm ${commissionFixedAmount === amount ? '' : 'p-button-outlined'}`}
                                    style={{ fontSize: '0.65rem' }}
                                    onClick={() => setCommissionFixedAmount(amount)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Milestone Based Model */}
                {commissionModel === 'milestone_based' && (
                    <div>
                        {availableMilestones.length === 0 ? (
                            <Message
                                severity="warn"
                                text="No milestones available for assignment."
                                className="w-full"
                            />
                        ) : (
                            <div className="flex flex-column gap-2">
                                {availableMilestones.map((milestone) => (
                                    <div
                                        key={milestone.id}
                                        className={`surface-100 border-round p-2 ${
                                            selectedMilestones.includes(milestone.id) ? 'border-2 border-primary' : ''
                                        }`}
                                    >
                                        <div className="flex align-items-center gap-2">
                                            <Checkbox
                                                checked={selectedMilestones.includes(milestone.id)}
                                                onChange={() => toggleMilestone(milestone.id)}
                                            />
                                            <div className="flex-1">
                                                <div className="font-medium" style={{ fontSize: '0.7rem' }}>{milestone.name}</div>
                                                {milestone.description && (
                                                    <div className="text-500" style={{ fontSize: '0.6rem' }}>{milestone.description}</div>
                                                )}
                                            </div>
                                            {selectedMilestones.includes(milestone.id) && (
                                                <InputNumber
                                                    value={milestoneAmounts[milestone.id] || 0}
                                                    onValueChange={(e) => updateMilestoneAmount(milestone.id, e.value)}
                                                    mode="currency"
                                                    currency="INR"
                                                    locale="en-IN"
                                                    className="w-8rem"
                                                    placeholder="Amount"
                                                    inputStyle={{ fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div className="flex justify-content-end pt-2 border-top-1 border-200">
                                    <span className="font-medium" style={{ fontSize: '0.7rem' }}>
                                        Total: <span className="text-green-600" style={{ fontSize: '0.85rem' }}>{formatCurrency(totalMilestoneAmount)}</span>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // Render Step 3: Additional Settings & Summary
    const renderStep3 = () => {
        const roleConfig = getRoleConfig(role);
        const contributionConfig = getContributionLevelConfig(contributionLevel);

        return (
            <div className="step-content">
                {/* Contribution Level */}
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2" style={{ fontSize: '0.75rem' }}>
                        <i className="pi pi-chart-bar mr-2" style={{ fontSize: '0.7rem' }} />
                        Expected Contribution Level
                    </label>
                    <Dropdown
                        value={contributionLevel}
                        options={CONTRIBUTION_OPTIONS}
                        onChange={(e) => setContributionLevel(e.value)}
                        optionLabel="label"
                        optionValue="value"
                        className="w-full"
                        style={{ fontSize: '0.7rem' }}
                        itemTemplate={(option) => (
                            <div className="py-1">
                                <div className="font-medium" style={{ fontSize: '0.7rem' }}>{option.label}</div>
                                <div className="text-500" style={{ fontSize: '0.6rem' }}>{option.description}</div>
                            </div>
                        )}
                    />
                </div>

                {/* Notes */}
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2" style={{ fontSize: '0.75rem' }}>
                        <i className="pi pi-comment mr-2" style={{ fontSize: '0.7rem' }} />
                        Message (Optional)
                    </label>
                    <InputTextarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        className="w-full"
                        placeholder="What do you expect from this collaborator? Any specific tasks or responsibilities..."
                        style={{ fontSize: '0.7rem' }}
                    />
                </div>

                <Divider className="my-2" />

                {/* Summary Preview */}
                <div className="surface-100 border-round p-3">
                    <div className="text-900 font-semibold m-0 mb-2" style={{ fontSize: '0.8rem' }}>
                        <i className="pi pi-file-check mr-2" style={{ fontSize: '0.75rem' }} />
                        Invitation Summary
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="mb-2">
                                <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Inviting</div>
                                <div className="flex align-items-center gap-2">
                                    <Avatar
                                        label={selectedAgent?.name?.charAt(0) || 'U'}
                                        shape="circle"
                                        className="bg-primary text-white"
                                        style={{ width: '1.5rem', height: '1.5rem', fontSize: '0.6rem' }}
                                    />
                                    <span className="font-semibold" style={{ fontSize: '0.75rem' }}>{selectedAgent?.name}</span>
                                </div>
                            </div>
                            <div className="mb-2">
                                <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Role</div>
                                <Tag
                                    value={roleConfig.label}
                                    icon={`pi ${roleConfig.icon}`}
                                    style={{
                                        backgroundColor: roleConfig.bgColor,
                                        color: roleConfig.color,
                                        border: 'none',
                                        fontSize: '0.6rem'
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="mb-2">
                                <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Member's Commission</div>
                                <div className="text-blue-500 font-bold" style={{ fontSize: '0.95rem' }}>
                                    {commissionModel === 'percentage_split' && `${100 - commissionPercentage}%`}
                                    {commissionModel === 'fixed_amount' && formatCurrency(commissionFixedAmount)}
                                    {commissionModel === 'milestone_based' && formatCurrency(totalMilestoneAmount)}
                                </div>
                                <div className="text-500" style={{ fontSize: '0.6rem' }}>
                                    {commissionModel === 'percentage_split' && `(You keep ${commissionPercentage}%)`}
                                    {commissionModel === 'fixed_amount' && 'fixed amount'}
                                    {commissionModel === 'milestone_based' && `${selectedMilestones.length} milestone(s)`}
                                </div>
                            </div>
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Contribution</div>
                                <Tag
                                    value={contributionConfig?.label || contributionLevel}
                                    style={{
                                        backgroundColor: contributionConfig?.bgColor,
                                        color: contributionConfig?.color,
                                        border: 'none',
                                        fontSize: '0.6rem'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {notes && (
                        <div className="mt-2 pt-2 border-top-1 border-200">
                            <div className="text-500 mb-1" style={{ fontSize: '0.65rem' }}>Message</div>
                            <div className="text-700 font-italic" style={{ fontSize: '0.7rem' }}>"{notes}"</div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Dialog footer
    const dialogFooter = (
        <div className="flex justify-content-between pt-2">
            <Button
                label={activeStep === 0 ? 'Cancel' : 'Back'}
                icon={activeStep === 0 ? 'pi pi-times' : 'pi pi-arrow-left'}
                className="p-button-text"
                style={{ fontSize: '0.7rem' }}
                onClick={() => {
                    if (activeStep === 0) {
                        onHide();
                    } else {
                        setActiveStep(activeStep - 1);
                    }
                }}
            />
            {activeStep < 2 ? (
                <Button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    style={{ fontSize: '0.7rem' }}
                    onClick={() => setActiveStep(activeStep + 1)}
                    disabled={activeStep === 0 ? !canProceedStep1 : !canProceedStep2()}
                />
            ) : (
                <Button
                    label="Send Invitation"
                    icon="pi pi-send"
                    className="p-button-success"
                    style={{ fontSize: '0.7rem' }}
                    onClick={handleSubmit}
                    loading={submitting}
                />
            )}
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={onHide}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user-plus text-primary" style={{ fontSize: '0.8rem' }} />
                        <span style={{ fontSize: '0.85rem' }}>Invite Collaborator</span>
                    </div>
                }
                style={{ width: '550px' }}
                modal
                className="p-fluid compact-invite-dialog"
                footer={dialogFooter}
                closable={!submitting}
                closeOnEscape={!submitting}
            >
                {/* Steps Indicator */}
                <Steps
                    model={steps}
                    activeIndex={activeStep}
                    readOnly
                    className="mb-3 compact-steps"
                />

                {/* Step Content */}
                <div className="step-container" style={{ minHeight: '250px' }}>
                    {activeStep === 0 && renderStep1()}
                    {activeStep === 1 && renderStep2()}
                    {activeStep === 2 && renderStep3()}
                </div>
            </Dialog>
        </>
    );
};

export default InviteMemberDialog;
